<script setup>
  import { computed, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { onClickOutside } from "@vueuse/core";
  import { Avatar, AvatarFallback } from "@/components/ui/avatar";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { Tooltip } from "@/components/ui/tooltip";
  import { X, CircleCheck, RotateCcw, Trash2, Pencil } from "lucide-vue-next";
  import CommentComposer from "./CommentComposer.vue";
  import { segmentMentions } from "./mentionSegments";
  import { relativeTimeFrom, shortDateTimeFrom } from "@utils/time";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";

  const store = useReviewCommentsStore();
  const { panel, threads } = storeToRefs(store);
  const cardsStore = useSurveyCardsStore();

  const panelRef = ref(null);
  onClickOutside(panelRef, () => store.closePanel(), {
    ignore: ["[data-comment-trigger]", "[data-reka-popper-content-wrapper]"],
  });

  const showResolved = ref(false);
  const editingCommentId = ref(null);
  const cardNameOf = (token) => cardsStore.findCardByToken?.({ token })?.name ?? "";
  const anchorLabel = (thread) => (thread.card_token === null ? "Survey" : cardNameOf(thread.card_token) || "Card");
  const effectiveCard = computed(() => (panel.value.showAll ? null : panel.value.contextCard));

  const chipClass = (active) =>
    active
      ? "h-auto rounded-full px-2.5 py-1 text-xs bg-primary/10 text-primary font-medium hover:bg-primary/10 hover:text-primary"
      : "h-auto rounded-full px-2.5 py-1 text-xs font-normal text-muted-foreground";

  const cardPositions = computed(() => new Map((cardsStore.cards ?? []).map((card, index) => [card.token, index])));
  const byCreation = (threadA, threadB) => new Date(threadA.created_at) - new Date(threadB.created_at);
  const byCardPosition = (threadA, threadB) =>
    (cardPositions.value.get(threadA.card_token) ?? Infinity) -
      (cardPositions.value.get(threadB.card_token) ?? Infinity) || byCreation(threadA, threadB);
  const visible = (list) => (showResolved.value ? list : list.filter((thread) => thread.status === "open"));

  const groups = computed(() => {
    const list = effectiveCard.value
      ? [{ key: "card", label: null, threads: visible(store.threadsForCard(effectiveCard.value)).sort(byCreation) }]
      : [
          {
            key: "general",
            label: "General comments",
            threads: visible(threads.value.filter((thread) => thread.card_token === null)).sort(byCreation),
          },
          {
            key: "cards",
            label: "Card comments",
            threads: visible(threads.value.filter((thread) => thread.card_token !== null)).sort(byCardPosition),
          },
        ];
    return list.filter((group) => group.threads.length);
  });

  const segmentsOf = (body) => segmentMentions(body, store.mentionableUsers);

  const saveEdit = async (thread, comment, body, mentions) => {
    await store.updateComment(thread, comment, body, mentions);
    editingCommentId.value = null;
  };

  const submitComposer = (body, mentions) => {
    if (effectiveCard.value) store.postToCard(effectiveCard.value, body, mentions);
    else store.postGeneral(body, mentions);
  };
  const composerPlaceholder = computed(() =>
    effectiveCard.value ? "Start a new thread on this card…" : "Start a general comment…",
  );
</script>

<template>
  <div
    ref="panelRef"
    class="absolute top-3 right-3 w-[380px] max-h-[82%] z-10 flex flex-col overflow-hidden rounded-xl border bg-white shadow-xl">
    <div class="flex items-center justify-between px-4 py-2.5 border-b shrink-0">
      <span class="font-semibold text-sm">Comments</span>
      <Button
        variant="ghost"
        size="icon"
        class="h-6 w-6 text-muted-foreground"
        @click="store.closePanel()">
        <X class="h-4 w-4" />
      </Button>
    </div>

    <div class="flex items-center gap-1.5 px-3 py-2 border-b shrink-0">
      <Button
        variant="ghost"
        size="sm"
        :class="chipClass(panel.showAll)"
        @click="store.setShowAll(true)">
        All
      </Button>
      <Button
        v-if="panel.contextCard"
        variant="ghost"
        size="sm"
        :class="chipClass(!panel.showAll)"
        @click="store.setShowAll(false)">
        {{ cardNameOf(panel.contextCard) || "This card" }}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        class="ml-auto"
        :class="chipClass(showResolved)"
        @click="showResolved = !showResolved">
        Show resolved
      </Button>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto p-2 space-y-2">
      <template
        v-for="group in groups"
        :key="group.key">
        <div
          v-if="group.label"
          class="px-1 pt-2 pb-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
          {{ group.label }}
        </div>
        <div
          v-for="thread in group.threads"
          :key="thread.id"
          class="rounded-lg border"
          :class="thread.status === 'resolved' ? 'opacity-70 bg-muted/30' : 'bg-white'">
          <div class="flex items-center gap-2 px-3 py-1.5 border-b">
            <Badge
              v-if="panel.showAll && thread.card_token !== null"
              variant="outline"
              class="text-3xs"
              >{{ anchorLabel(thread) }}</Badge
            >
            <Badge
              v-if="thread.status === 'resolved'"
              variant="success"
              class="text-3xs"
              >Resolved</Badge
            >
            <Button
              v-if="thread.status === 'open'"
              variant="ghost"
              size="sm"
              class="ml-auto h-6 gap-1 px-2 text-xs font-normal text-muted-foreground hover:bg-status-success-soft hover:text-status-success-strong"
              @click="store.resolveThread(thread)">
              <CircleCheck class="h-3.5 w-3.5" />Resolve
            </Button>
            <Button
              v-else
              variant="ghost"
              size="sm"
              class="ml-auto h-6 gap-1 px-2 text-xs font-normal text-muted-foreground"
              @click="store.reopenThread(thread)">
              <RotateCcw class="h-3.5 w-3.5" />Reopen
            </Button>
          </div>

          <div class="px-3 py-2 space-y-3">
            <div
              v-for="comment in thread.review_comments"
              :key="comment.id"
              class="group flex gap-2">
              <Avatar
                class="h-6 w-6 shrink-0"
                :class="{ 'opacity-50': comment.deleted }">
                <AvatarFallback class="text-3xs bg-secondary text-foreground-secondary">{{
                  comment.user.initials
                }}</AvatarFallback>
              </Avatar>
              <div class="min-w-0 flex-1">
                <div class="text-3xs text-muted-foreground">
                  {{ comment.user.full_name }}
                  <span
                    v-if="comment.posted_via"
                    class="italic"
                    >via {{ comment.posted_via }}</span
                  >
                  · {{ relativeTimeFrom(comment.created_at) }}
                  <Tooltip
                    v-if="comment.edited_at"
                    :content="`Edited ${shortDateTimeFrom(comment.edited_at)}`">
                    <span>· (edited)</span>
                  </Tooltip>
                </div>
                <p
                  v-if="comment.deleted"
                  class="text-sm italic text-muted-foreground">
                  Deleted comment
                </p>
                <CommentComposer
                  v-else-if="editingCommentId === comment.id"
                  mode="edit"
                  :initial-body="comment.body"
                  @submit="(body, mentions) => saveEdit(thread, comment, body, mentions)"
                  @cancel="editingCommentId = null" />
                <p
                  v-else
                  class="text-sm whitespace-pre-wrap break-words">
                  <template
                    v-for="(segment, index) in segmentsOf(comment.body)"
                    :key="index"
                    ><span
                      v-if="segment.mention"
                      class="text-status-info-strong font-medium"
                      >{{ segment.text }}</span
                    ><span v-else>{{ segment.text }}</span></template
                  >
                </p>
              </div>
              <template v-if="store.isMine(comment) && !comment.deleted && editingCommentId !== comment.id">
                <Tooltip content="Edit comment">
                  <Button
                    variant="ghost"
                    size="icon"
                    class="self-start mt-0.5 h-6 w-6 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-primary"
                    @click="editingCommentId = comment.id">
                    <Pencil class="h-3.5 w-3.5" />
                  </Button>
                </Tooltip>
                <Tooltip content="Delete comment">
                  <Button
                    variant="ghost"
                    size="icon"
                    class="self-start mt-0.5 h-6 w-6 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-status-error-strong"
                    @click="store.deleteComment(thread, comment)">
                    <Trash2 class="h-3.5 w-3.5" />
                  </Button>
                </Tooltip>
              </template>
            </div>
          </div>

          <div class="px-3 pb-3">
            <CommentComposer
              :placeholder="thread.status === 'resolved' ? 'Reply to reopen…' : 'Reply…'"
              @submit="(body, mentions) => store.addComment(thread, body, mentions)" />
          </div>
        </div>
      </template>

      <EmptyState
        v-if="!groups.length"
        title="No comments yet" />
    </div>

    <div class="p-3 border-t shrink-0">
      <CommentComposer
        :placeholder="composerPlaceholder"
        @submit="submitComposer" />
    </div>
  </div>
</template>
